import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trophy, Search, User, Shield, Flag, CircleDot, Car, X, BarChart3 } from "lucide-react";
import { getCountryFlag } from "@/lib/countryFlags";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

const PODIUM_COLORS: Record<number, { text: string; border: string }> = {
  1: { text: "#FFD700", border: "rgba(255, 215, 0, 0.45)" },
  2: { text: "#C0C0C0", border: "rgba(192, 192, 192, 0.45)" },
  3: { text: "#CD7F32", border: "rgba(205, 127, 50, 0.45)" },
};

const AVATARS: Record<string, any> = {
  helmet: Shield,
  flag: Flag,
  wheel: CircleDot,
  car: Car,
  trophy: Trophy,
};

function MemberAvatar({ avatarIcon, teamColour }: { avatarIcon: string | null; teamColour: string | null }) {
  const Icon = (avatarIcon && AVATARS[avatarIcon]) || User;
  const color = teamColour ? `#${teamColour}` : "hsl(var(--muted-foreground))";
  return (
    <div
      className="flex h-9 w-9 items-center justify-center rounded-full border-2 flex-shrink-0"
      style={{ borderColor: color, backgroundColor: `${color}20` }}
    >
      <Icon size={16} style={{ color }} />
    </div>
  );
}

interface TippingChampionshipProps {
  leagueId: string;
  isOwner?: boolean;
}

export function TippingChampionship({ leagueId, isOwner }: TippingChampionshipProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRace, setSelectedRace] = useState<string>("season");
  const [search, setSearch] = useState("");
  const [removeMember, setRemoveMember] = useState<{ userId: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);

  // Fetch races with resolved matchups for 2026 season
  const { data: resolvedRaces = [] } = useQuery({
    queryKey: ["resolved-races-2026"],
    queryFn: async () => {
      // Get race IDs that have at least one resolved matchup
      const { data: resolvedMatchups, error: mErr } = await supabase
        .from("matchups")
        .select("race_id")
        .eq("status", "resolved");
      if (mErr) throw mErr;
      const resolvedRaceIds = [...new Set(resolvedMatchups?.map(m => m.race_id) ?? [])];
      if (resolvedRaceIds.length === 0) return [];

      const { data, error } = await supabase
        .from("races")
        .select("id, round_number, meeting_name, country_name, date_start, seasons!inner(year)")
        .eq("seasons.year", 2026)
        .in("id", resolvedRaceIds)
        .order("round_number", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch standings
  const raceId = selectedRace === "season" ? null : selectedRace;
  const { data: standings = [], isLoading } = useQuery({
    queryKey: ["league-standings", leagueId, raceId],
    queryFn: async () => {
      const params: { p_league_id: string; p_race_id?: string } = { p_league_id: leagueId };
      if (raceId) params.p_race_id = raceId;
      const { data, error } = await supabase.rpc("get_league_standings", params);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Client-side search filter
  const filtered = useMemo(() => {
    if (!search.trim()) return standings;
    const q = search.toLowerCase();
    return standings.filter((s) => s.display_name?.toLowerCase().includes(q));
  }, [standings, search]);

  const allZero = standings.length > 0 && standings.every((s) => s.total_points === 0);

  const handleRemoveMember = async () => {
    if (!removeMember) return;
    setRemoving(true);
    const { error } = await supabase
      .from("league_members")
      .delete()
      .eq("league_id", leagueId)
      .eq("user_id", removeMember.userId);
    setRemoving(false);
    if (error) {
      toast({ title: "Failed to remove member", description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["league-standings", leagueId] });
      queryClient.invalidateQueries({ queryKey: ["league-members", leagueId] });
      queryClient.invalidateQueries({ queryKey: ["league", leagueId] });
      toast({ title: `${removeMember.name} removed from league.` });
    }
    setRemoveMember(null);
  };

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Tipping Championship</h2>

      {/* Round filter */}
      <div className="flex items-center gap-2">
        <Select value={selectedRace} onValueChange={setSelectedRace}>
          <SelectTrigger>
            <SelectValue placeholder="Season 2026" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="season">Season 2026</SelectItem>
            {resolvedRaces.map((race) => (
              <SelectItem key={race.id} value={race.id}>
                Round {race.round_number} — {race.meeting_name} {getCountryFlag(race.country_name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 flex-shrink-0"
          title="View detailed results"
          onClick={() => {
            const params = new URLSearchParams({ league: leagueId });
            if (selectedRace !== "season") params.set("race", selectedRace);
            navigate(`/results?${params.toString()}`);
          }}
        >
          <BarChart3 className="h-4 w-4" />
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Standings */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-6">Loading standings…</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
            <Trophy className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No members found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((member) => {
            const rank = standings.findIndex((s) => s.user_id === member.user_id) + 1;
            const showRank = !allZero && rank > 0;
            const podium = showRank && rank <= 3 ? PODIUM_COLORS[rank] : undefined;
            const isCurrentUser = member.user_id === user?.id;
            const rawDelta = member.pole_margin_delta != null ? Number(member.pole_margin_delta) : 0;
            const deltaInSeconds = rawDelta / 1000;
            const marginDelta = deltaInSeconds.toFixed(3);
            const canRemove = isOwner && member.user_id !== user?.id;

            return (
              <Card
                key={member.user_id}
                className={cn(isCurrentUser && "ring-1 ring-primary/30 bg-primary/5")}
                style={podium ? { borderColor: podium.border, borderWidth: 2 } : undefined}
              >
                <CardContent className="flex items-center gap-3 p-3">
                  {/* Rank */}
                  <span
                    className="text-sm font-bold w-8 text-center flex-shrink-0"
                    style={podium ? { color: podium.text } : undefined}
                  >
                    {showRank ? `P${rank}` : "—"}
                  </span>

                  {/* Avatar */}
                  <MemberAvatar
                    avatarIcon={member.avatar_icon}
                    teamColour={member.team_colour}
                  />

                  {/* Name + remove */}
                  <div className="flex-1 min-w-0 flex items-center gap-1">
                    <p className="font-medium text-sm truncate">{member.display_name || "Unknown"}</p>
                    {canRemove && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5 flex-shrink-0 text-muted-foreground/50 hover:text-destructive"
                        onClick={() => setRemoveMember({ userId: member.user_id, name: member.display_name || "Unknown" })}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-center w-10">
                      <p className="text-base font-bold leading-tight">{member.correct_picks ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">tips</p>
                    </div>
                    <div className="text-center w-10">
                      <p className="text-xs font-mono leading-tight">{marginDelta}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">margin</p>
                    </div>
                  </div>


                </CardContent>
              </Card>
            );
          })}

          {/* Footer message for pre-season */}
          {allZero && (
            <p className="text-xs text-muted-foreground text-center pt-2 pb-1">
              Leaderboard updates after each race — first round is the Australian GP on March 8.
            </p>
          )}
        </div>
      )}

      {/* Remove member confirmation */}
      <AlertDialog open={!!removeMember} onOpenChange={(open) => !open && setRemoveMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeMember?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {removeMember?.name} from this league. They can rejoin using the invite code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} disabled={removing}>
              {removing ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
